import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  Type,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, convertToParamMap, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { FormRuntimeRendererComponent } from '../dynamic-form/form-runtime-renderer.component';
import { AinPageComponent } from '../design-system/page.component';
import { AinStateComponent } from '../design-system/state.component';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CalendarService } from '../../core/localization/calendar.service';
import { currentAppScope } from '../../core/scope/app-scope';
import { DynamicApiService } from '../../features/dynamic/dynamic-api.service';
import { bindWorkflowTaskLiveUpdates } from '../../features/workflow/workflow-live-updates';
import { ProcessTaskDetail } from '../../features/workflow/workflow-api.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { WorkflowModuleSurfaceRegistry } from './workflow-module-surface.registry';
import { WorkflowSurfaceFallbackComponent } from './workflow-surface-fallback.component';
import {
  WORKFLOW_TASK_CONTEXT,
  WORKFLOW_TASK_SURFACE_HOST,
  WorkflowTaskContext,
  WorkflowTaskDecision,
  WorkflowTaskSurfaceComponent,
  WorkflowTaskSurfaceHostAdapter,
  WorkflowTaskSurfaceOutcome,
  buildWorkflowTaskContext
} from './workflow-task-context';
import { WORKFLOW_TASK_OPERATIONS, WorkflowTaskOperations } from './workflow-task-operations';
import { buildTaskCompletionOutput, mergeSurfaceOutcome } from './workflow-task-outcome.util';
import { BehaviorSubject, Subject, catchError, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type SurfaceLoadState = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

@Component({
  standalone: true,
  selector: 'ain-workflow-task-host',
  imports: [
    AinPageComponent,
    AinStateComponent,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    FormsModule,
    RouterLink,
    TagModule,
    NgComponentOutlet,
    NgTemplateOutlet,
    WorkflowSurfaceFallbackComponent,
    FormRuntimeRendererComponent
  ],
  providers: [MessageService],
  template: `
    <ng-template #taskBody>
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (loadError()) {
        <ain-state type="error" [title]="loadError() ?? t('common.loadFailed')" />
      } @else if (context(); as ctx) {
        <div class="task-meta">
          <p-tag [value]="taskStatusLabel(ctx.taskStatus)" />
          @if (ctx.dueDateUtc) {
            <span>{{ t('workflow.taskHost.due') }}: {{ formatWhen(ctx.dueDateUtc) }}</span>
          }
          @if (showAssignee(ctx.assignedUserId)) {
            <span>{{ t('workflow.taskHost.assignee') }}: {{ ctx.assignedUserId }}</span>
          }
          @if (ctx.boundEntityName) {
            <span>{{ t('workflow.taskHost.boundEntity') }}: {{ ctx.boundEntityName }}</span>
          }
        </div>

        <div class="toolbar">
          @if (ctx.permissions.canClaim) {
            <button pButton type="button" [label]="t('workflow.claim')" (click)="claim()"></button>
          }
          @if (showAdvancedActions()) {
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('workflow.delegate')"
              [disabled]="!ctx.permissions.canDelegate"
              (click)="delegate()"></button>
            <button
              pButton
              type="button"
              severity="warn"
              [label]="t('workflow.escalate')"
              [disabled]="!ctx.permissions.canDelegate"
              (click)="escalate()"></button>
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('workflow.addComment')"
              (click)="addComment()"></button>
          }
          @if (showEscapeButton()) {
            <button
              pButton
              type="button"
              severity="help"
              [label]="t('workflow.taskHost.openInModule')"
              (click)="openEscapeHatch()"></button>
          }
        </div>

        @if (showAdvancedActions()) {
          <div class="inline-fields">
            <input pInputText [(ngModel)]="delegateToUserId" [placeholder]="t('workflow.delegateUserId')" />
            <input pInputText [(ngModel)]="escalationReason" [placeholder]="t('workflow.escalationReason')" />
            <input pInputText [(ngModel)]="commentBody" [placeholder]="t('workflow.comment')" />
          </div>
        }

        <section class="surface-panel">
          @if (ctx.operatorType === 'ModuleSurface') {
            @switch (surfaceLoadState()) {
              @case ('loading') {
                <ain-state type="loading" [title]="t('workflow.taskHost.loadingSurface')" />
              }
              @case ('missing') {
                <ain-workflow-surface-fallback
                  [title]="t('workflow.surfaces.fallback.notRegisteredTitle')"
                  [message]="t('workflow.surfaces.fallback.notRegisteredMessage')"
                  [surfaceConfiguration]="ctx.surfaceConfiguration"
                  [showEscape]="showEscapeButton()" />
              }
              @case ('error') {
                <ain-workflow-surface-fallback
                  [title]="t('workflow.surfaces.fallback.loadFailedTitle')"
                  [message]="surfaceLoadError() ?? t('workflow.surfaces.fallback.loadFailedMessage')"
                  [surfaceConfiguration]="ctx.surfaceConfiguration"
                  [showEscape]="showEscapeButton()" />
              }
              @case ('ready') {
                @if (surfaceComponentType(); as componentType) {
                  <ng-container *ngComponentOutlet="componentType; injector: surfaceInjector!" />
                }
              }
            }
          } @else if (ctx.operatorType === 'Form' && ctx.formDefinitionId) {
            @if (formSchemaLoading()) {
              <ain-state type="loading" [title]="t('workflow.taskHost.loadingForm')" />
            } @else if (formSchemaError()) {
              <ain-state type="error" [title]="formSchemaError() ?? t('common.loadFailed')" />
            } @else {
              <ain-form-runtime-renderer
                #formRenderer
                [schemaJson]="formSchemaSnapshot()"
                mode="runtime"
                [lookupSearchEnabled]="true"
                [showSubmitFooter]="false"
                [initialValues]="ctx.formData"
                (valuesChange)="onFormValuesChange($event)" />
            }
          } @else {
            <section class="user-task-panel">
              <p>{{ t('workflow.taskHost.userTaskGuidance') }}</p>
              <label>{{ t('workflow.taskHost.reason') }}</label>
              <textarea pTextarea rows="4" [(ngModel)]="reason" class="w-full"></textarea>
            </section>
          }
        </section>

        @if (!isTerminal(ctx.taskStatus)) {
          <footer class="outcome-footer">
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('workflow.actions.reject')"
              [disabled]="!ctx.permissions.canReject || completing()"
              (click)="completeWithDecision('rejected')"></button>
            <button
              pButton
              type="button"
              severity="warn"
              [label]="t('workflow.actions.requestChanges')"
              [disabled]="!ctx.permissions.canComplete || completing()"
              (click)="completeWithDecision('changesRequested')"></button>
            <button
              pButton
              type="button"
              severity="success"
              [label]="t('workflow.taskHost.approveAndComplete')"
              [disabled]="!ctx.permissions.canComplete || completing() || !surfaceReady()"
              (click)="completeWithDecision('approved')"></button>
          </footer>
        } @else {
          <ain-state type="empty" [title]="t('workflow.taskHost.completedState')" />
        }

        @if (showDebugPanel()) {
          <details class="debug-panel">
            <summary>{{ t('workflow.taskHost.debugOutput') }}</summary>
            <pre>{{ debugOutput() }}</pre>
          </details>
        }
      }
    </ng-template>

    @if (embedded) {
      <div class="embedded-host">
        <ng-container *ngTemplateOutlet="taskBody" />
      </div>
    } @else {
      <ain-page
        [title]="context()?.taskName ?? t('workflow.tasks.title')"
        [description]="context()?.taskDescription ?? ''">
        <a routerLink="/workflow/tasks">{{ t('workflow.taskHost.backToInbox') }}</a>
        <ng-container *ngTemplateOutlet="taskBody" />
      </ain-page>
    }
  `,
  styles: [`
    a { display: inline-block; margin-bottom: 12px; color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .task-meta { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; color: #6b7280; }
    .toolbar, .outcome-footer { display: flex; gap: 8px; flex-wrap: wrap; margin: 12px 0; }
    .inline-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 8px; margin-bottom: 12px; }
    .surface-panel { margin: 16px 0; padding-top: 12px; border-top: 1px solid var(--ain-color-border, #e5e7eb); }
    .user-task-panel { display: grid; gap: 8px; }
    .w-full { width: 100%; }
    .debug-panel { margin-top: 16px; }
    pre { white-space: pre-wrap; word-break: break-word; background: #f9fafb; padding: 12px; border-radius: 8px; }
    .embedded-host { display: grid; gap: 8px; }
  `]
})
export class WorkflowTaskHostComponent implements OnInit, OnChanges {
  private readonly operations = inject(WORKFLOW_TASK_OPERATIONS);
  private readonly registry = inject(WorkflowModuleSurfaceRegistry);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly calendar = inject(CalendarService);
  private readonly messages = inject(MessageService);
  private readonly router = inject(Router);
  private readonly parentInjector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);

  @Input({ required: true }) taskId = '';
  @Input() embedded = false;
  @Output() taskCompleted = new EventEmitter<void>();

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly context = signal<WorkflowTaskContext | null>(null);
  readonly completing = signal(false);
  readonly surfaceLoadState = signal<SurfaceLoadState>('idle');
  readonly surfaceLoadError = signal<string | null>(null);
  readonly surfaceComponentType = signal<Type<unknown> | null>(null);
  readonly formSchemaLoading = signal(false);
  readonly formSchemaError = signal<string | null>(null);
  readonly formSchemaSnapshot = signal('{}');
  readonly debugOutput = signal('{}');
  readonly surfaceReady = signal(true);

  surfaceInjector: Injector | null = null;
  @ViewChild(NgComponentOutlet) private surfaceOutlet?: NgComponentOutlet;

  delegateToUserId = '';
  escalationReason = '';
  commentBody = '';
  reason = '';
  formValues: Record<string, unknown> = {};
  private pendingSurfaceOutcome: WorkflowTaskSurfaceOutcome | null = null;
  private surfaceHostAdapter: WorkflowTaskSurfaceHostAdapter | null = null;
  private readonly loadRequests = new Subject<{ id: string; showLoading: boolean }>();

  ngOnInit(): void {
    this.loadRequests.pipe(
      switchMap(request =>
        this.operations.getTask(request.id).pipe(
          map(task => ({ id: request.id, task })),
          catchError(() => of({ id: request.id, task: null as ProcessTaskDetail | null }))
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      if (result.id !== this.taskId) {
        return;
      }

      if (!result.task) {
        this.loading.set(false);
        this.loadError.set(this.t('common.loadFailed'));
        return;
      }

      const ctx = buildWorkflowTaskContext(result.task);
      this.context.set(ctx);
      this.debugOutput.set(result.task.outputDataJson || '{}');
      this.loading.set(false);
      this.loadError.set(null);
      void this.initializeOperatorView(ctx, result.task);
    });

    this.reload(true);
    bindWorkflowTaskLiveUpdates(
      {
        liveUpdateService: this.liveUpdateService,
        contextService: this.contextService,
        destroyRef: this.destroyRef
      },
      showLoading => this.reload(showLoading),
      () => this.taskId
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['taskId'];
    if (!change || change.firstChange) {
      return;
    }

    const next = String(change.currentValue ?? '');
    const previous = String(change.previousValue ?? '');
    if (!next || next === previous) {
      return;
    }

    this.resetTransientWorkState();
    this.reload(true);
  }

  showAdvancedActions(): boolean {
    if (this.embedded) {
      return false;
    }
    return currentAppScope() !== 'organization';
  }

  showDebugPanel(): boolean {
    if (this.embedded) {
      return false;
    }
    return currentAppScope() !== 'organization';
  }

  showEscapeButton(): boolean {
    const ctx = this.context();
    if (!ctx?.surfaceConfiguration?.allowFullPageEscape) {
      return false;
    }

    return !!this.resolveEscapeRoute(ctx);
  }

  openEscapeHatch(): void {
    const ctx = this.context();
    const route = ctx ? this.resolveEscapeRoute(ctx) : null;
    if (!route) {
      return;
    }

    const hasQuery = route.includes('?');
    const separator = hasQuery ? '&' : '?';
    const target = `${route}${separator}fromTaskId=${encodeURIComponent(this.taskId)}`;
    void this.router.navigateByUrl(target);
  }

  onFormValuesChange(values: Record<string, unknown>): void {
    this.formValues = values;
  }

  claim(): void {
    this.operations.claimTask(this.taskId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.claimed') });
        this.reload(false);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  delegate(): void {
    if (!this.delegateToUserId.trim()) {
      return;
    }

    this.operations.delegateTask(this.taskId, this.delegateToUserId.trim(), 'admin-delegate').subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.delegated') });
        this.reload(false);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  escalate(): void {
    this.operations.escalateTask(this.taskId, undefined, this.escalationReason).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('workflow.escalated') });
        this.reload(false);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  addComment(): void {
    if (!this.commentBody.trim()) {
      return;
    }

    this.operations.addComment(this.taskId, this.commentBody.trim()).subscribe({
      next: () => {
        this.commentBody = '';
        this.messages.add({ severity: 'success', summary: this.t('workflow.commentAdded') });
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  async completeWithDecision(decision: WorkflowTaskDecision): Promise<void> {
    const ctx = this.context();
    if (!ctx || this.completing()) {
      return;
    }

    const intercepted = await this.invokeSurfaceBeforeComplete(decision);
    if (intercepted === null) {
      return;
    }
    if (intercepted) {
      this.pendingSurfaceOutcome = intercepted;
    }

    const payload: Record<string, unknown> = {};
    if (ctx.operatorType === 'Form') {
      payload['formData'] = this.formValues;
    }

    const output = this.pendingSurfaceOutcome && decision === this.pendingSurfaceOutcome.decision
      ? mergeSurfaceOutcome(this.pendingSurfaceOutcome, this.reason, payload)
      : buildTaskCompletionOutput(decision, this.reason, payload);

    this.debugOutput.set(output);
    this.completing.set(true);
    this.operations.completeTask(this.taskId, output).subscribe({
      next: () => {
        this.completing.set(false);
        this.messages.add({ severity: 'success', summary: this.t('workflow.completed') });
        this.taskCompleted.emit();
        this.reload(false);
      },
      error: () => {
        this.completing.set(false);
        this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') });
      }
    });
  }

  isTerminal(status: string): boolean {
    const normalized = status.trim().toLowerCase();
    return normalized === 'completed' || normalized === 'cancelled' || normalized === '4' || normalized === '5';
  }

  taskStatusLabel(status: string): string {
    const key = `workflow.instances.runtime.taskStatus.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  formatWhen(value?: string): string {
    return value ? this.calendar.formatDate(value, { hour: '2-digit', minute: '2-digit' }) : '—';
  }

  showAssignee(assignedUserId?: string): boolean {
    if (this.embedded || !assignedUserId) {
      return false;
    }
    return !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(assignedUserId);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private reload(showLoading = false): void {
    if (!this.taskId) {
      return;
    }

    if (showLoading) {
      this.loading.set(true);
      this.loadError.set(null);
    }

    this.loadRequests.next({ id: this.taskId, showLoading });
  }

  private resetTransientWorkState(): void {
    this.context.set(null);
    this.completing.set(false);
    this.surfaceLoadState.set('idle');
    this.surfaceLoadError.set(null);
    this.surfaceComponentType.set(null);
    this.formSchemaLoading.set(false);
    this.formSchemaError.set(null);
    this.formSchemaSnapshot.set('{}');
    this.debugOutput.set('{}');
    this.surfaceReady.set(true);
    this.surfaceInjector = null;
    this.delegateToUserId = '';
    this.escalationReason = '';
    this.commentBody = '';
    this.reason = '';
    this.formValues = {};
    this.pendingSurfaceOutcome = null;
    this.surfaceHostAdapter = null;
  }

  private async initializeOperatorView(ctx: WorkflowTaskContext, task: ProcessTaskDetail): Promise<void> {
    const expectedTaskId = this.taskId;
    if (ctx.operatorType === 'ModuleSurface') {
      await this.loadModuleSurface(ctx, expectedTaskId);
      return;
    }

    if (ctx.operatorType === 'Form' && ctx.formDefinitionId) {
      this.loadFormSchema(ctx.formDefinitionId, expectedTaskId);
      return;
    }

    if (this.taskId !== expectedTaskId) {
      return;
    }

    this.surfaceLoadState.set('idle');
    this.surfaceComponentType.set(null);
    this.surfaceInjector = null;
    if (task.outputDataJson?.trim()) {
      this.reason = this.extractReason(task.outputDataJson);
    }
  }

  private async loadModuleSurface(ctx: WorkflowTaskContext, expectedTaskId: string): Promise<void> {
    const moduleKey = ctx.moduleKey ?? ctx.surfaceConfiguration?.moduleKey ?? '';
    const surfaceKey = ctx.surfaceKey ?? ctx.surfaceConfiguration?.surfaceKey ?? '';
    if (!moduleKey || !surfaceKey) {
      this.surfaceLoadState.set('missing');
      return;
    }

    const registration = this.registry.resolve(moduleKey, surfaceKey);
    if (!registration) {
      this.surfaceLoadState.set('missing');
      return;
    }

    const missingContextKey = (registration.requiredContextKeys ?? []).find(key => {
      const value = this.resolveRequiredContextValue(ctx, key);
      return value === undefined || value === null || value === '';
    });
    if (missingContextKey) {
      this.surfaceLoadError.set(this.t('workflow.surfaces.fallback.contextMissingMessage'));
      this.surfaceLoadState.set('error');
      return;
    }

    this.surfaceLoadState.set('loading');
    try {
      const componentType = await registration.loadComponent();
      if (this.taskId !== expectedTaskId) {
        return;
      }

      this.pendingSurfaceOutcome = null;
      this.surfaceHostAdapter = new WorkflowTaskSurfaceHostAdapter(
        outcome => {
          this.pendingSurfaceOutcome = outcome;
          if (outcome.reason) {
            this.reason = outcome.reason;
          }
        },
        () => this.openEscapeHatch()
      );
      this.surfaceReady.set(true);
      this.surfaceHostAdapter.readiness$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(ready => this.surfaceReady.set(ready));

      this.surfaceInjector = Injector.create({
        parent: this.parentInjector,
        providers: [
          { provide: WORKFLOW_TASK_CONTEXT, useValue: ctx },
          { provide: WORKFLOW_TASK_SURFACE_HOST, useValue: this.surfaceHostAdapter },
          { provide: ActivatedRoute, useValue: this.buildEmbeddedActivatedRoute(ctx) }
        ]
      });

      this.surfaceComponentType.set(componentType);
      this.surfaceLoadState.set('ready');
      this.surfaceLoadError.set(null);
    } catch (error) {
      if (this.taskId !== expectedTaskId) {
        return;
      }

      this.surfaceLoadError.set(error instanceof Error ? error.message : this.t('workflow.surfaces.fallback.loadFailedMessage'));
      this.surfaceLoadState.set('error');
    }
  }

  private loadFormSchema(formDefinitionId: string, expectedTaskId: string): void {
    this.formSchemaLoading.set(true);
    this.formSchemaError.set(null);
    this.dynamicApi.getFormRuntime(formDefinitionId).subscribe({
      next: runtime => {
        if (this.taskId !== expectedTaskId) {
          return;
        }

        this.formSchemaSnapshot.set(runtime.schemaSnapshot || '{}');
        this.formSchemaLoading.set(false);
      },
      error: () => {
        if (this.taskId !== expectedTaskId) {
          return;
        }

        this.formSchemaLoading.set(false);
        this.formSchemaError.set(this.t('workflow.taskHost.formLoadFailed'));
      }
    });
  }

  private extractReason(outputDataJson: string): string {
    try {
      const parsed = JSON.parse(outputDataJson) as { reason?: string };
      return parsed.reason ?? '';
    } catch {
      return '';
    }
  }

  private async invokeSurfaceBeforeComplete(
    decision: WorkflowTaskDecision
  ): Promise<WorkflowTaskSurfaceOutcome | null | undefined> {
    const instance = this.surfaceOutlet?.componentInstance as WorkflowTaskSurfaceComponent | undefined;
    const hook = instance?.beforeHostComplete;
    if (!hook) {
      return undefined;
    }

    try {
      const result = await hook.call(instance, decision);
      return result ?? undefined;
    } catch {
      this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') });
      return null;
    }
  }

  private resolveEscapeRoute(ctx: WorkflowTaskContext): string | null {
    const template = this.normalizeRouteTemplate(ctx.surfaceConfiguration?.fullPageRouteTemplate);
    if (!template) {
      return null;
    }

    const values = this.buildRouteTemplateValues(ctx);
    const params = this.extractRouteParams(template, values);

    return template.replace(/:([A-Za-z0-9_]+)/g, (_full, key: string) => {
      const value = params[key];
      if (!value) {
        return `:${key}`;
      }

      return encodeURIComponent(value);
    });
  }

  private buildEmbeddedActivatedRoute(ctx: WorkflowTaskContext): ActivatedRoute {
    const template = this.normalizeRouteTemplate(ctx.surfaceConfiguration?.fullPageRouteTemplate);
    const values = this.buildRouteTemplateValues(ctx);
    const params = this.extractRouteParams(template, values);
    const queryParams: Params = { fromTaskId: this.taskId };

    const paramMap = convertToParamMap(params);
    const queryParamMap = convertToParamMap(queryParams);
    const paramsSubject = new BehaviorSubject<Params>(params);
    const queryParamsSubject = new BehaviorSubject<Params>(queryParams);
    const paramMapSubject = new BehaviorSubject<ParamMap>(paramMap);
    const queryParamMapSubject = new BehaviorSubject<ParamMap>(queryParamMap);

    const route = {
      snapshot: {
        params,
        queryParams,
        paramMap,
        queryParamMap,
        data: {},
        url: [],
        fragment: null
      } as any,
      params: paramsSubject.asObservable(),
      queryParams: queryParamsSubject.asObservable(),
      paramMap: paramMapSubject.asObservable(),
      queryParamMap: queryParamMapSubject.asObservable(),
      data: of({}),
      url: of([]),
      fragment: of(null),
      title: of(undefined),
      outlet: 'primary',
      component: null,
      routeConfig: null,
      root: null as any,
      parent: null,
      firstChild: null,
      pathFromRoot: [] as ActivatedRoute[],
      children: []
    } as unknown as ActivatedRoute;

    (route as any).root = route;
    (route as any).pathFromRoot = [route];
    return route;
  }

  private normalizeRouteTemplate(rawTemplate?: string): string {
    const raw = rawTemplate?.trim();
    if (!raw) {
      return '';
    }

    return raw.startsWith('/') ? raw : `/${raw}`;
  }

  private buildRouteTemplateValues(ctx: WorkflowTaskContext): Record<string, string> {
    const values: Record<string, string> = {};
    for (const [key, value] of Object.entries(ctx.variables || {})) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      values[key.toLowerCase()] = `${value}`;
    }

    if (ctx.boundEntityId) {
      values['boundentityid'] = ctx.boundEntityId;
      values['entityid'] = ctx.boundEntityId;
      values['id'] = ctx.boundEntityId;
    }

    values['taskid'] = ctx.taskId;
    values['processinstanceid'] = ctx.processInstanceId;
    return values;
  }

  private extractRouteParams(template: string, values: Readonly<Record<string, string>>): Record<string, string> {
    const params: Record<string, string> = {};
    if (!template) {
      return params;
    }

    const matches = template.matchAll(/:([A-Za-z0-9_]+)/g);
    for (const match of matches) {
      const key = match[1];
      const lowerKey = key.toLowerCase();
      const value = values[lowerKey]
        ?? (lowerKey.endsWith('id') ? values['boundentityid'] : undefined);
      if (value) {
        params[key] = value;
      }
    }

    return params;
  }

  private resolveRequiredContextValue(ctx: WorkflowTaskContext, key: string): unknown {
    if (key === 'boundEntityId' && ctx.boundEntityId) {
      return ctx.boundEntityId;
    }

    const variables = ctx.variables as Record<string, unknown>;
    const direct = this.readCaseInsensitive(variables, key);
    if (direct !== undefined && direct !== null && direct !== '') {
      return direct;
    }

    const payloadRaw = this.readCaseInsensitive(variables, 'payload');
    if (payloadRaw && typeof payloadRaw === 'object' && !Array.isArray(payloadRaw)) {
      const payload = payloadRaw as Record<string, unknown>;
      const fromPayload = this.readCaseInsensitive(payload, key);
      if (fromPayload !== undefined && fromPayload !== null && fromPayload !== '') {
        return fromPayload;
      }
    }

    return undefined;
  }

  private readCaseInsensitive(record: Record<string, unknown>, key: string): unknown {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return record[key];
    }

    const normalized = key.toLowerCase();
    for (const [entryKey, entryValue] of Object.entries(record)) {
      if (entryKey.toLowerCase() === normalized) {
        return entryValue;
      }
    }

    return undefined;
  }
}
